import { Button, Divider, Drawer, Dropdown, Flex, Form, notification, Tag, Typography } from "antd";
import { ApiService } from "api/api.service";
import { useState } from "react";
import { TbHistory, TbLoader2, TbNote, TbPlayerPlayFilled } from "react-icons/tb";
import { queryClient } from "utils/queryClient";
import { T_Filters } from "../LeadCases.utils";
import { CASE_STATUS_COLOURS, CASE_STATUS_ICONS } from "utils/constants";
import { UpdateForm } from "./UpdateForm";
import { Sidebar } from "./Sidebar";
import { MutationKey } from "components/CroppedTimeline";

export function DetailsDrawer({
  caseId,
  setCaseId,
  filters,
}: {
  caseId: string;
  setCaseId: (val: string | undefined) => void;
  filters: T_Filters;
}) {
  const [isSaved, setIsSaved] = useState(false);
  const [updateForm] = Form.useForm();
  const [activeSection, setActiveSection] = useState<"logs" | "remarks">("logs");
  const [logMutationKey, setLogMutationKey] = useState<MutationKey>(false);
  const [remarksMutationKey, setRemarksMutationKey] = useState<MutationKey>(false);

  const runCases = ApiService.lead.useRunCases();
  const getCaseById = ApiService.lead.useGetLeadCaseById(caseId, ["logs", "remarks"]);
  const patchCase = ApiService.lead.useUpdateCase();
  const viewDrawerData = getCaseById.data?.result;

  function handleRun(method: "split" | "pc" | "bc" | "pcpl" | "discard", caseIds: string[]) {
    runCases.mutate(
      {
        method,
        case_ids: caseIds,
        rerun: true,
      },
      {
        onSuccess: (data, variables) => {
          setIsSaved(true);
          if (variables.method === "pc") {
            return queryClient.invalidateQueries({ queryKey: ["lead", "case", caseId] });
          } else if (variables.method === "bc") {
            notification.info({
              message: "Requested case is processing. Please refresh and check after a few minutes",
            });
          }
        },
      },
    );
  }

  return (
    <Drawer
      styles={{
        body: { height: "100%", padding: 0 },
      }}
      width={800}
      title={
        <Flex align="center" className="justify-between">
          <Typography.Text>{viewDrawerData?.defendant ?? ""}</Typography.Text>
          <Button
            className="w-40"
            variant="solid"
            icon={activeSection === "logs" ? <TbNote /> : <TbHistory />}
            color={activeSection === "logs" ? "blue" : "pink"}
            onClick={() => {
              if (activeSection === "logs" && logMutationKey) setLogMutationKey(false);
              else if (activeSection === "remarks" && remarksMutationKey) setRemarksMutationKey(false);
              setActiveSection((prev) => (prev === "logs" ? "remarks" : "logs"));
            }}
          >
            Show {activeSection === "logs" ? "Remarks" : "Logs"}
          </Button>
        </Flex>
      }
      open={!!caseId}
      onClose={() => {
        if (isSaved) {
          queryClient.invalidateQueries({ queryKey: ["lead", "case", filters] });
        }
        setIsSaved(false);
        updateForm.resetFields();
        setCaseId(undefined);
      }}
      footer={
        viewDrawerData ? (
          viewDrawerData?.hold ? (
            <div className="progress_loader w-full p-2 text-center">Case is Processing</div>
          ) : (
            <div className="flex gap-2">
              <Button
                className="flex-grow"
                type="primary"
                loading={patchCase.isPending}
                onClick={() => {
                  updateForm.submit();
                }}
              >
                Update
              </Button>
              <Dropdown
                menu={{
                  items: [
                    // { label: "Extract Field", key: "split" },
                    { label: "Enrich LinkedIn", key: "pc" },
                    { label: "Enrich Contact Info", key: "bc" },
                  ],
                  onClick: (info) => {
                    handleRun(info.key as any, [caseId]);
                  },
                }}
              >
                <Button
                  variant="solid"
                  color="default"
                  className="!bg-green-600 !border-green-600 hover:!bg-green-500 hover:!border-green-500 flex-grow"
                  icon={<TbPlayerPlayFilled />}
                  loading={runCases.isPending}
                >
                  Re-Run
                </Button>
              </Dropdown>
            </div>
          )
        ) : null
      }
    >
      {getCaseById?.isFetching ? (
        <div className="flex py-16 px-5 items-center justify-center">
          <TbLoader2 className="animate-spin" />
        </div>
      ) : getCaseById?.error ? (
        <div className="flex py-16 px-5 items-center justify-center">
          <Typography.Text>{getCaseById?.error?.message}</Typography.Text>
        </div>
      ) : getCaseById?.data && viewDrawerData ? (
        <div className="flex overflow-y-hidden h-full">
          <div className="flex flex-col gap-3 p-5 flex-grow !w-2/3 overflow-y-auto hide-scrollbar">
            <div className="inline-flex flex-col gap-1 w-fit">
              <Typography.Text type="secondary">Summary</Typography.Text>
              <Typography.Paragraph className="!m-0">{viewDrawerData["summary"] ?? "-"}</Typography.Paragraph>
            </div>
            <div className="inline-flex flex-col gap-1 w-fit">
              <Typography.Text type="secondary">Defendant</Typography.Text>
              <Typography.Paragraph className="!m-0">{viewDrawerData["defendant"] ?? "-"}</Typography.Paragraph>
            </div>
            <div className="grid grid-cols-2 gap-1">
              <div className="inline-flex flex-col gap-1 w-fit">
                <Typography.Text type="secondary">Status</Typography.Text>
                <Tag icon={CASE_STATUS_ICONS[viewDrawerData.status]} color={CASE_STATUS_COLOURS[viewDrawerData.status]}>
                  {viewDrawerData.status?.toUpperCase()}
                </Tag>
              </div>
              <div className="inline-flex flex-col gap-1 w-fit">
                <Typography.Text type="secondary">Last Step</Typography.Text>
                <Tag>{viewDrawerData.step?.toUpperCase()}</Tag>
              </div>
            </div>

            <Divider className="!my-3" />
            <UpdateForm
              data={viewDrawerData}
              form={updateForm}
              onSuccess={() => {
                setIsSaved(true);
              }}
            />
          </div>
          <Sidebar
            caseId={viewDrawerData._id}
            logs={viewDrawerData.logs ?? []}
            remarks={viewDrawerData.remarks ?? []}
            activeSection={activeSection}
            logMutationKey={logMutationKey}
            setLogMutationKey={setLogMutationKey}
            remarksMutationKey={remarksMutationKey}
            setRemarksMutationKey={setRemarksMutationKey}
            refetch={getCaseById.refetch}
          />
        </div>
      ) : null}
    </Drawer>
  );
}
